import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import CheckIcon from '@mui/icons-material/Check'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'

import { opsApi } from '../../api/endpoints'
import { useFormatters } from '../../hooks/useFormatters'
import { EmptyState, ErrorState } from '../../components/common/States'
import type { OrderStatus, ServiceOrderDto } from '../../types/api'

const STATUSES: OrderStatus[] = ['REQUESTED', 'CONFIRMED', 'DELIVERED', 'CANCELLED']

const STATUS_COLOR: Record<OrderStatus, 'warning' | 'info' | 'success' | 'default'> = {
  REQUESTED: 'warning',
  CONFIRMED: 'info',
  DELIVERED: 'success',
  CANCELLED: 'default',
}

/**
 * What the kitchen has to make, and when.
 *
 * Ordered by the time the guest asked for rather than by when the order arrived: an order
 * placed this morning for tonight matters less than one placed ten minutes ago for lunch.
 * Every time on this page is the estate's own clock, so an owner reading it from abroad sees
 * the hour the guest will actually be sitting down.
 */
export function AdminOrdersPage() {
  const { t } = useTranslation()
  const { moneyPrecise, dateTimeAtEstate } = useFormatters()
  const queryClient = useQueryClient()

  const [status, setStatus] = useState<OrderStatus | undefined>(undefined)
  const [cancelling, setCancelling] = useState<ServiceOrderDto | null>(null)
  const [ownerNote, setOwnerNote] = useState('')

  const orders = useQuery({
    queryKey: ['admin', 'orders', status ?? 'open'],
    queryFn: () => (status ? opsApi.orders(status) : opsApi.openOrders()),
    // The owner leaves this page open on a tablet in the kitchen.
    refetchInterval: 60_000,
  })

  const update = useMutation({
    mutationFn: ({ id, next, note }: { id: string; next: OrderStatus; note?: string }) =>
      opsApi.updateOrderStatus(id, next, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] })
      // A delivered order changes what the guest owes.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'balance'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'ops', 'dashboard'] })
    },
  })

  const rows = [...(orders.data ?? [])].sort(
    (a, b) => new Date(a.requestedFor).getTime() - new Date(b.requestedFor).getTime(),
  )

  const confirmCancel = () => {
    if (!cancelling) return
    update.mutate({ id: cancelling.id, next: 'CANCELLED', note: ownerNote || undefined })
    setCancelling(null)
    setOwnerNote('')
  }

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('admin.orders')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.ordersIntro')}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          label={t('admin.ordersOpen')}
          onClick={() => setStatus(undefined)}
          color={status === undefined ? 'primary' : 'default'}
          variant={status === undefined ? 'filled' : 'outlined'}
        />
        {STATUSES.map((option) => (
          <Chip
            key={option}
            label={t(`order.status${option}`)}
            onClick={() => setStatus(option)}
            color={status === option ? 'primary' : 'default'}
            variant={status === option ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>

      {orders.isError && <ErrorState error={orders.error} onRetry={() => void orders.refetch()} />}

      {!orders.isLoading && rows.length === 0 && (
        <EmptyState icon="🍽️" title={t('admin.noOrders')} body={t('admin.noOrdersBody')} />
      )}

      <Stack spacing={2}>
        {rows.map((order) => (
          <Paper
            key={order.id}
            elevation={0}
            sx={{
              p: 2.5,
              border: 1,
              borderColor: order.status === 'REQUESTED' ? 'primary.main' : 'divider',
              borderRadius: 3,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="h5" component="p">
                    {dateTimeAtEstate(order.requestedFor)}
                  </Typography>
                  <Chip
                    size="small"
                    label={t(`order.status${order.status}`)}
                    color={STATUS_COLOR[order.status]}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {order.guestName} · {order.unitName} · {order.bookingReference}
                </Typography>
              </Box>
              <Typography variant="h5" component="p" sx={{ whiteSpace: 'nowrap' }}>
                {moneyPrecise(order.totalAmount)}
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={0.5} sx={{ mb: order.guestNote ? 1.5 : 2 }}>
              {order.lines.map((line) => (
                <Stack key={line.id} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2">
                    {line.quantity} × {line.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {moneyPrecise(line.lineTotal)}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            {order.guestNote && (
              <Paper
                elevation={0}
                sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}
              >
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('admin.guestNote')}
                </Typography>
                <Typography variant="body2">{order.guestNote}</Typography>
              </Paper>
            )}

            {order.ownerNote && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                {t('admin.ownerNote')}: {order.ownerNote}
              </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {order.status === 'REQUESTED' && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CheckIcon />}
                  disabled={update.isPending}
                  onClick={() => update.mutate({ id: order.id, next: 'CONFIRMED' })}
                >
                  {t('admin.acceptOrder')}
                </Button>
              )}
              {(order.status === 'REQUESTED' || order.status === 'CONFIRMED') && (
                <>
                  <Button
                    variant={order.status === 'CONFIRMED' ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<RestaurantIcon />}
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: order.id, next: 'DELIVERED' })}
                  >
                    {t('admin.markDelivered')}
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<CloseIcon />}
                    disabled={update.isPending}
                    onClick={() => {
                      setCancelling(order)
                      setOwnerNote('')
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </>
              )}
              {order.status === 'DELIVERED' && (
                <Typography variant="caption" color="success.main">
                  ✅ {t('admin.addedToBill')}
                </Typography>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>

      {/* Cancelling is the one action a guest feels, so it asks for a reason they can be told. */}
      <Dialog open={cancelling !== null} onClose={() => setCancelling(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('admin.cancelOrder')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.cancelOrderBody')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={t('admin.ownerNote')}
            value={ownerNote}
            onChange={(event) => setOwnerNote(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelling(null)}>{t('common.back')}</Button>
          <Button onClick={confirmCancel} color="error" variant="contained">
            {t('admin.cancelOrder')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
